import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { wsChannel } from '@xdocs/schemas';
import type { ErrorAlert, ExplorerEntry } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

export interface UseExplorerFileEntryResult {
  entry: ExplorerEntry | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/** Single-entry read (`file.get`) for the F7 preview pane/route, kept live
 * off the `user:{userId}` explorer-event channel (mirrors `useDocument.ts`'s
 * "signal, not payload" re-fetch-on-push convention). */
export function useExplorerFileEntry(fileId: string | null): UseExplorerFileEntryResult {
  const { wsClient, connectionState } = useWs();
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && fileId !== null;

  const query = useQuery<ExplorerEntry, ErrorAlert>({
    queryKey: ['explorer-entry', fileId],
    queryFn: () => requestTyped(wsClient, 'file.get', { fileId: fileId as string }),
    enabled,
  });

  useEffect(() => {
    if (!userId || connectionState !== 'connected') return undefined;
    const channel = wsChannel.user(userId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['explorer-entry', fileId] });
    });
  }, [wsClient, queryClient, userId, connectionState, fileId]);

  return {
    entry: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  };
}
